import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Music, Save, LogOut, Lock, Eye, EyeOff, Camera, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [formData, setFormData] = useState({
    artist_name: '',
    full_name: '',
    email: '',
    phone: '',
    about_me: '',
    keep_name_private: false,
    profile_picture: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    if (!userData) {
      navigate('/');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Pre-fill form with existing data
    setFormData({
      artist_name: parsedUser.artist_name || '',
      full_name: parsedUser.full_name || parsedUser.name || '',
      email: parsedUser.email || '',
      phone: parsedUser.phone || '',
      about_me: parsedUser.about_me || '',
      keep_name_private: parsedUser.keep_name_private || false,
      profile_picture: parsedUser.profile_picture || ''
    });
    setProfilePicture(parsedUser.profile_picture || null);
    
    setLoading(false);
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const response = await axios.put(`${API}/users/profile`, formData);
      
      // Update local storage with new info
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          canvas.toBlob(
            (blob) => {
              const compressedReader = new FileReader();
              compressedReader.onloadend = () => {
                const compressedSize = (compressedReader.result.length * 0.75) / 1024; // Approximate size in KB
                console.log(`Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${compressedSize.toFixed(2)}KB`);
                resolve(compressedReader.result);
              };
              compressedReader.readAsDataURL(blob);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setUploadingPhoto(true);
      
      // Show processing message for large files
      if (file.size > 2 * 1024 * 1024) {
        toast.info('Compressing image...');
      }

      // Compress the image
      const compressedImage = await compressImage(file);
      
      // Check compressed size
      const compressedSize = (compressedImage.length * 0.75) / 1024 / 1024; // Size in MB
      
      if (compressedSize > 5) {
        // If still too large, compress more aggressively
        const moreCompressed = await compressImage(file, 600, 600, 0.6);
        setProfilePicture(moreCompressed);
        
        // Update in database
        await axios.put(`${API}/users/profile`, {
          email: user.email,
          profile_picture: moreCompressed
        });

        // Update localStorage
        const updatedUser = { ...user, profile_picture: moreCompressed };
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        setUser(updatedUser);
      } else {
        setProfilePicture(compressedImage);
        
        // Update in database
        await axios.put(`${API}/users/profile`, {
          email: user.email,
          profile_picture: compressedImage
        });

        // Update localStorage
        const updatedUser = { ...user, profile_picture: compressedImage };
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      
      await axios.post(`${API}/auth/change-password`, {
        email: user.email,
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Password changed successfully!');
      
      // Reset password fields
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordSection(false);
      
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    const stayLoggedIn = localStorage.getItem('stay_logged_in') === 'true';
    if (!stayLoggedIn) {
      localStorage.removeItem('user_data');
      localStorage.removeItem('auth_token');
    }
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              My Profile
            </h1>
            <p className="text-gray-400 mt-2">Manage your account information</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>

        {/* Profile Form */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-8 space-y-6">
          {/* Profile Picture */}
          <div className="flex flex-col items-center gap-4 pb-6 border-b border-purple-500/20">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User size={48} className="text-white" />
                )}
              </div>
              <label
                htmlFor="profile-picture-upload"
                className="absolute bottom-0 right-0 bg-purple-600 hover:bg-purple-700 rounded-full p-2 cursor-pointer transition-colors"
              >
                {uploadingPhoto ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <Camera size={20} className="text-white" />
                )}
              </label>
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploadingPhoto}
              />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-400">Click the camera icon to upload a profile picture</p>
              <p className="text-xs text-gray-500 mt-1">Any size accepted - we'll automatically compress large images</p>
            </div>
          </div>

          {/* Artist Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <Music size={18} className="text-purple-400" />
              Artist Name
            </label>
            <input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleChange}
              placeholder="Your stage/artist name"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <User size={18} className="text-purple-400" />
              Full Name
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Your legal/full name"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            
            {/* Preview of how name will appear */}
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-300 mb-2"><strong>📋 How your name will appear on receipts:</strong></p>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="show_full_name"
                  checked={!formData.keep_name_private}
                  onChange={(e) => setFormData({ ...formData, keep_name_private: !e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-gray-800 border-purple-500 rounded focus:ring-purple-500"
                />
                <label htmlFor="show_full_name" className="text-sm text-gray-300 cursor-pointer">
                  Show both names on receipts
                </label>
              </div>
              <div className="mt-2 p-2 bg-black/40 rounded text-white text-sm">
                {!formData.keep_name_private && formData.artist_name && formData.full_name
                  ? `${formData.artist_name} (${formData.full_name})`
                  : formData.artist_name || 'Artist Name'}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {!formData.keep_name_private 
                  ? '✓ Both names will show on receipts and invoices'
                  : '🔒 Only artist name will be public. Full name stays private.'}
              </p>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <Mail size={18} className="text-purple-400" />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <Phone size={18} className="text-purple-400" />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* About Me */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <User size={18} className="text-purple-400" />
              About Me
            </label>
            <textarea
              name="about_me"
              value={formData.about_me}
              onChange={handleChange}
              placeholder="Tell us about yourself, your music style, achievements, etc..."
              rows={5}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">This helps us provide better service tailored to your needs</p>
          </div>

          {/* Change Password Section */}
          <div className="border-t border-purple-500/20 pt-6">
            <button
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              className="flex items-center gap-2 text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors"
            >
              <Lock size={18} />
              <span>{showPasswordSection ? 'Hide Password Change' : 'Change Password'}</span>
            </button>

            {showPasswordSection && (
              <div className="mt-4 space-y-4 p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      placeholder="Enter current password"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Change Password Button */}
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="w-full py-3 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      <span>Changing Password...</span>
                    </>
                  ) : (
                    <>
                      <Lock size={20} />
                      <span>Change Password</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={20} />
                <span>Save Profile</span>
              </>
            )}
          </button>

          {/* Info Note */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-300">
              <strong>⚡ Faster Checkout:</strong> Your information is securely saved and will automatically fill in during booking checkout, giving you a seamless and quick reservation experience every time!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
